/**
 * Module SAV - Modal creation
 * 13/05/2026 - feature/module-sav
 *
 * Toggle Bijou a nous (lookup) / Bijou externe (saisie libre)
 * Toggle Client existant (search) / Nouveau (saisie libre)
 * Tarif : Gratuit / Sous garantie / Payant
 * Upload photos AVANT sequentiel
 */
import React, { useState, useEffect } from 'react';
import { API_URL } from '../../services/api';
import { X, Search, Camera } from 'lucide-react';

export default function SAVCreateModal({ onClose, onCreated }) {
  // Article
  const [modeArticle, setModeArticle] = useState('interne'); // interne | externe
  const [bijouSearch, setBijouSearch] = useState('');
  const [bijouFound, setBijouFound] = useState(null);
  const [bijouError, setBijouError] = useState('');
  const [extNom, setExtNom] = useState('');
  const [extDesc, setExtDesc] = useState('');
  const [extCategorie, setExtCategorie] = useState('autre');
  const [extMatiere, setExtMatiere] = useState('or');
  const [extCouleur, setExtCouleur] = useState('JAUNE');
  const [extPoids, setExtPoids] = useState('');

  // Type
  const [typeSav, setTypeSav] = useState('REPARATION');

  // Client
  const [modeClient, setModeClient] = useState('existant'); // existant | nouveau
  const [clientSearch, setClientSearch] = useState('');
  const [clientList, setClientList] = useState([]);
  const [clientId, setClientId] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientNom, setClientNom] = useState('');
  const [clientTel, setClientTel] = useState('');
  const [bijouxAchetes, setBijouxAchetes] = useState([]);
  const [loadingBijoux, setLoadingBijoux] = useState(false);

  // Description
  const [description, setDescription] = useState('');

  // Tarif
  const [tarifType, setTarifType] = useState('PAYANT');
  const [prix, setPrix] = useState('');

  // Promesse
  const [datePromise, setDatePromise] = useState('');
  const [notesAdmin, setNotesAdmin] = useState('');

  // Photos
  const [photos, setPhotos] = useState([]); // File objects

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Lookup bijou interne
  const lookupBijou = async () => {
    if (!bijouSearch.trim()) return;
    setBijouError('');
    setBijouFound(null);
    try {
      const res = await fetch(`${API_URL}/bijoux/lookup?q=${encodeURIComponent(bijouSearch.trim())}`);
      if (res.ok) {
        const data = await res.json();
        const results = data.results || (Array.isArray(data) ? data : []);
        if (results.length > 0) {
          setBijouFound(results[0]);
        } else {
          setBijouError('Bijou introuvable');
        }
      } else {
        setBijouError('Bijou introuvable');
      }
    } catch (e) {
      setBijouError('Erreur recherche');
    }
  };

  // Search clients
  useEffect(() => {
    if (modeClient !== 'existant' || !clientSearch.trim() || clientSearch.length < 2) {
      setClientList([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/clients?search=${encodeURIComponent(clientSearch)}`);
        const data = await res.json();
        setClientList(Array.isArray(data) ? data.slice(0, 10) : []);
      } catch (e) {
        setClientList([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch, modeClient]);

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - photos.length);
    setPhotos([...photos, ...files].slice(0, 3));
  };

  const removePhoto = (i) => {
    setPhotos(photos.filter((_, j) => j !== i));
  };

  // Auto-lookup bijou (debounce 400ms) si modeArticle === 'interne'
  useEffect(() => {
    if (modeArticle !== 'interne' || !bijouSearch.trim() || bijouSearch.length < 2) return;
    const timer = setTimeout(() => {
      lookupBijou();
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bijouSearch, modeArticle]);

  const handleSelectClient = async (c) => {
    setSelectedClient(c);
    setClientId(c.id);
    setClientSearch(c.nom || '');
    setClientList([]);
    setLoadingBijoux(true);
    setBijouxAchetes([]);
    try {
      const res = await fetch(`${API_URL}/clients/${c.id}/bijoux-achetes`);
      if (res.ok) {
        const data = await res.json();
        setBijouxAchetes(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      setBijouxAchetes([]);
    } finally {
      setLoadingBijoux(false);
    }
  };

  const handleSelectBijouAchete = (b) => {
    setModeArticle('interne');
    setBijouFound(b);
    setBijouSearch(b.id_produit || b.reference_article || '');
    setBijouError('');
  };

  const resetClient = () => {
    setSelectedClient(null);
    setClientId(null);
    setClientSearch('');
    setClientList([]);
    setBijouxAchetes([]);
  };

  const submit = async () => {
    setError('');

    // Validation
    if (modeArticle === 'interne' && !bijouFound) {
      setError('Sélectionner un bijou interne');
      return;
    }
    if (modeArticle === 'externe' && !extNom.trim()) {
      setError('Nom article externe requis');
      return;
    }
    if (!description.trim()) {
      setError('Description du travail requise');
      return;
    }
    if (modeClient === 'existant' && !clientId) {
      setError('Sélectionner un client');
      return;
    }
    if (modeClient === 'nouveau' && !clientNom.trim()) {
      setError('Nom client requis');
      return;
    }

    const payload = {
      type_sav: typeSav,
      description_travail: description,
      tarif_type: tarifType,
      prix: tarifType === 'PAYANT' ? (parseFloat(prix) || 0) : 0,
      date_promise: datePromise || null,
      notes_admin: notesAdmin || null
    };

    if (modeArticle === 'interne') {
      payload.bijou_id = bijouFound.id;
    } else {
      payload.article_externe_nom = extNom;
      payload.article_externe_description = extDesc;
      payload.article_externe_categorie = extCategorie;
      payload.article_externe_matiere = extMatiere;
      payload.article_externe_couleur_or = extCouleur;
      payload.article_externe_poids = parseFloat(extPoids) || 0;
    }

    setSubmitting(true);

    // Mode nouveau client : creer client d'abord puis reutiliser id
    if (modeClient === 'existant') {
      payload.client_id = clientId;
    } else {
      try {
        const resC = await fetch(`${API_URL}/clients`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            nom: clientNom.trim(),
            telephone: clientTel.trim() || null,
            type_client: 'particulier'
          })
        });
        const dataC = await resC.json();
        if (!resC.ok) {
          setError('Creation client: ' + (dataC.error || 'erreur'));
          setSubmitting(false);
          return;
        }
        payload.client_id = dataC.id;
        payload.client_nom = clientNom.trim();
        payload.client_telephone = clientTel.trim() || null;
      } catch (e) {
        setError('Erreur creation client: ' + e.message);
        setSubmitting(false);
        return;
      }
    }

    try {
      const res = await fetch(`${API_URL}/sav/dossiers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Erreur creation SAV');
        setSubmitting(false);
        return;
      }
      const savId = data.id;

      // Upload photos sequentiel
      for (const f of photos) {
        const fd = new FormData();
        fd.append('photo', f);
        fd.append('type_photo', 'AVANT');
        await fetch(`${API_URL}/sav/dossiers/${savId}/photos`, {
          method: 'POST',
          body: fd
        });
      }

      onCreated(savId);
    } catch (e) {
      setError('Erreur reseau: ' + e.message);
      setSubmitting(false);
    }
  };

  const tarifBtnCls = (k, activeCls) => `flex-1 py-2 rounded-md text-xs font-semibold transition-all duration-200 ${
    tarifType === k ? `${activeCls} text-white shadow-md hover:scale-105` : 'bg-white text-stone-600 border border-stone-200 hover:bg-stone-50 hover:scale-105'
  }`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[94vh] overflow-y-auto">

        {/* HEADER luxe noir + amber */}
        <div className="px-6 py-4 flex justify-between items-center bg-gradient-to-r from-stone-900 via-amber-900 to-stone-900 rounded-t-2xl border-b border-amber-500/30">
          <h2 className="text-xl font-bold text-white flex items-center gap-3 tracking-wide">
            <span className="text-2xl">🔧</span> Nouveau dossier SAV
          </h2>
          <button onClick={onClose} className="text-white hover:bg-amber-500/20 rounded-full p-1.5 transition-all">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">

          {/* TYPE SAV ligne compact - accents premium */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-semibold text-stone-700 min-w-[55px]">Type :</label>
            <div className="flex gap-1.5">
              {[
                { val: 'REPARATION', label: '🔧 Reparation', activeCls: 'bg-amber-600' },
                { val: 'DEVIS', label: '📋 Devis', activeCls: 'bg-stone-700' },
                { val: 'AUTRE', label: '⚙️ Autre', activeCls: 'bg-rose-500' }
              ].map(t => (
                <button key={t.val} type="button" onClick={() => setTypeSav(t.val)}
                        className={`px-4 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                          typeSav === t.val
                            ? `${t.activeCls} text-white shadow-md hover:scale-105`
                            : 'bg-stone-100 text-stone-600 hover:bg-stone-200 hover:scale-105'
                        }`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* 2 COLONNES : ARTICLE | CLIENT */}
          <div className="grid grid-cols-2 gap-5">

            {/* ARTICLE - amber jaune dore */}
            <div className="bg-gradient-to-br from-amber-50 to-yellow-100 rounded-xl p-5 border-2 border-amber-300 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-base font-bold text-amber-900 mb-4 flex items-center gap-3 tracking-wide">
                <span className="text-2xl">💎</span>
                <span>ARTICLE</span>
              </h3>

              {/* Toggle */}
              <div className="flex gap-1 p-1 bg-white rounded-lg mb-3">
                <button type="button" onClick={() => setModeArticle('interne')}
                        className={`flex-1 py-1.5 rounded text-xs font-medium transition-all duration-200 ${
                          modeArticle === 'interne' ? 'bg-amber-500 text-white shadow' : 'text-stone-600 hover:bg-stone-50'
                        }`}>
                  Bijou a nous
                </button>
                <button type="button" onClick={() => setModeArticle('externe')}
                        className={`flex-1 py-1.5 rounded text-xs font-medium transition-all duration-200 ${
                          modeArticle === 'externe' ? 'bg-amber-500 text-white shadow' : 'text-stone-600 hover:bg-stone-50'
                        }`}>
                  Bijou externe
                </button>
              </div>

              {modeArticle === 'interne' && (
                <>
                  <div className="relative">
                    <input value={bijouSearch}
                           onChange={e => setBijouSearch(e.target.value)}
                           onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), lookupBijou())}
                           placeholder="ID produit ou reference"
                           className="w-full pl-3 pr-8 py-1.5 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 outline-none" />
                    <Search className="absolute right-2 top-2 w-4 h-4 text-amber-400" />
                  </div>
                  {bijouError && <div className="text-xs text-red-600 mt-1">{bijouError}</div>}
                  {bijouFound && (
                    <div className="mt-2 p-2 bg-white rounded border border-green-300">
                      <div className="font-bold text-sm">{bijouFound.nom}</div>
                      <div className="text-xs text-gray-600 font-mono">
                        {bijouFound.id_produit || bijouFound.reference_article}
                        {bijouFound.poids ? ` - ${bijouFound.poids}g` : ''}
                      </div>
                    </div>
                  )}
                </>
              )}

              {modeArticle === 'externe' && (
                <div className="space-y-1.5">
                  <input value={extNom} onChange={e => setExtNom(e.target.value)}
                         placeholder="Nom article *"
                         className="w-full p-1.5 border border-amber-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-amber-400" />
                  <div className="grid grid-cols-3 gap-1.5">
                    <select value={extCategorie} onChange={e => setExtCategorie(e.target.value)}
                            className="p-1.5 border border-amber-300 rounded-lg text-xs">
                      <option value="bague">Bague</option>
                      <option value="collier">Collier</option>
                      <option value="bracelet">Bracelet</option>
                      <option value="boucles_oreilles">B.Oreilles</option>
                      <option value="pendentif">Pendentif</option>
                      <option value="autre">Autre</option>
                    </select>
                    <select value={extMatiere} onChange={e => setExtMatiere(e.target.value)}
                            className="p-1.5 border border-amber-300 rounded-lg text-xs">
                      <option value="or">Or</option>
                      <option value="argent">Argent</option>
                      <option value="plaque">Plaque</option>
                      <option value="autre">Autre</option>
                    </select>
                    <input type="number" step="0.01" value={extPoids}
                           onChange={e => setExtPoids(e.target.value)}
                           placeholder="Poids g"
                           className="p-1.5 border border-amber-300 rounded-lg text-xs" />
                  </div>
                  <textarea rows="2" value={extDesc}
                            onChange={e => setExtDesc(e.target.value)}
                            placeholder="Description (optionnel)"
                            className="w-full p-1.5 border border-amber-300 rounded-lg text-sm" />
                </div>
              )}
            </div>

            {/* CLIENT - accent rose-gold */}
            <div className="bg-gradient-to-br from-rose-50 to-amber-50 rounded-xl p-5 border-2 border-rose-200 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-base font-bold text-rose-900 mb-4 flex items-center gap-3 tracking-wide">
                <span className="text-2xl">👤</span>
                <span>CLIENT</span>
              </h3>

              {/* Toggle */}
              <div className="flex gap-1 p-1 bg-white rounded-lg mb-3">
                <button type="button" onClick={() => { setModeClient('existant'); resetClient(); }}
                        className={`flex-1 py-1.5 rounded text-xs font-medium transition-all duration-200 ${
                          modeClient === 'existant' ? 'bg-rose-600 text-white shadow' : 'text-stone-600 hover:bg-stone-50'
                        }`}>
                  Existant
                </button>
                <button type="button" onClick={() => { setModeClient('nouveau'); resetClient(); }}
                        className={`flex-1 py-1.5 rounded text-xs font-medium transition-all duration-200 ${
                          modeClient === 'nouveau' ? 'bg-rose-600 text-white shadow' : 'text-stone-600 hover:bg-stone-50'
                        }`}>
                  Nouveau
                </button>
              </div>

              {modeClient === 'existant' && (
                <div className="relative">
                  <input value={clientSearch} onChange={e => {
                           setClientSearch(e.target.value);
                           if (selectedClient) {
                             setSelectedClient(null);
                             setClientId(null);
                             setBijouxAchetes([]);
                           }
                         }}
                         placeholder="Tapez nom, telephone..."
                         className="w-full pl-3 pr-8 py-2 border border-rose-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-rose-400 focus:border-rose-500" />
                  <Search className="absolute right-2 top-2.5 w-4 h-4 text-rose-400" />

                  {clientList.length > 0 && !selectedClient && (
                    <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-rose-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                      {clientList.map(c => (
                        <button key={c.id} type="button"
                                onClick={() => handleSelectClient(c)}
                                className="w-full text-left p-2 hover:bg-rose-50 border-b last:border-0 text-sm transition-colors">
                          <div className="font-semibold">{c.nom}</div>
                          {c.telephone && <div className="text-xs text-stone-500">{c.telephone}</div>}
                        </button>
                      ))}
                    </div>
                  )}

                  {selectedClient && (
                    <div className="mt-2 p-3 bg-white rounded-lg border-2 border-rose-300 flex justify-between items-start shadow-sm">
                      <div>
                        <div className="font-bold text-sm">{selectedClient.nom}</div>
                        <div className="text-xs text-stone-500">{selectedClient.telephone || '-'}</div>
                      </div>
                      <button type="button" onClick={resetClient}
                              className="text-rose-600 text-xs hover:underline font-semibold">Changer</button>
                    </div>
                  )}

                  {/* Bijoux achetes - badge garantie precis */}
                  {selectedClient && (loadingBijoux || bijouxAchetes.length > 0) && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-stone-700 mb-1.5 flex items-center gap-1.5">
                        <span>💎</span> Achats recents {loadingBijoux ? '(chargement...)' : `(${bijouxAchetes.length})`}
                      </p>
                      <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                        {bijouxAchetes.map(b => {
                          const moisRestants = b.jours_depuis_achat != null
                            ? Math.max(0, Math.ceil((365 - b.jours_depuis_achat) / 30))
                            : 0;
                          const selected = bijouFound?.id === b.id;
                          return (
                            <button key={b.id} type="button"
                                    onClick={() => handleSelectBijouAchete(b)}
                                    className={`w-full text-left px-2.5 py-2 rounded-lg text-xs flex justify-between items-center transition-all duration-200 hover:scale-[1.01] ${
                                      selected
                                        ? 'bg-amber-100 border-2 border-amber-400 shadow-sm'
                                        : 'bg-white hover:bg-amber-50 border border-stone-200'
                                    }`}>
                              <div className="truncate">
                                <span className="font-mono font-bold text-stone-900">{b.id_produit}</span>
                                <span className="ml-2 text-stone-700">{b.nom}</span>
                              </div>
                              <div className="text-right whitespace-nowrap ml-2 flex flex-col gap-0.5 items-end">
                                <span className="text-stone-500">{b.date_achat_str || '-'}</span>
                                {b.sous_garantie ? (
                                  <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full text-[10px] font-semibold">
                                    ✓ Garantie {moisRestants}m
                                  </span>
                                ) : (
                                  <span className="bg-stone-200 text-stone-600 px-2 py-0.5 rounded-full text-[10px]">
                                    Hors garantie
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {modeClient === 'nouveau' && (
                <div className="space-y-2">
                  <input value={clientNom} onChange={e => setClientNom(e.target.value)}
                         placeholder="Nom *"
                         className="w-full p-2 border border-rose-300 rounded-lg text-sm focus:ring-2 focus:ring-rose-400 focus:border-rose-500 outline-none" />
                  <input value={clientTel} onChange={e => setClientTel(e.target.value)}
                         placeholder="Telephone"
                         className="w-full p-2 border border-rose-300 rounded-lg text-sm focus:ring-2 focus:ring-rose-400 focus:border-rose-500 outline-none" />
                </div>
              )}
            </div>
          </div>

          {/* TRAVAIL + TARIF + DATE compact */}
          <div className="bg-stone-50 rounded-xl p-5 space-y-3 border border-stone-200">

            <div>
              <label className="text-xs font-semibold text-gray-700 mb-1 block">
                📋 Travail demande <span className="text-red-500">*</span>
              </label>
              <textarea rows="2" value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder="Ex: Reparer fermoir + polir..."
                        className="w-full p-2.5 border border-amber-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-500 outline-none" />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="text-xs font-semibold text-gray-700 mb-1 block">💰 Tarif</label>
                <div className="flex gap-1">
                  <button type="button" onClick={() => setTarifType('GRATUIT')}
                          className={tarifBtnCls('GRATUIT', 'bg-emerald-500')}>
                    Gratuit
                  </button>
                  <button type="button" onClick={() => setTarifType('SOUS_GARANTIE')}
                          className={tarifBtnCls('SOUS_GARANTIE', 'bg-amber-500')}>
                    Garantie
                  </button>
                  <button type="button" onClick={() => setTarifType('PAYANT')}
                          className={tarifBtnCls('PAYANT', 'bg-rose-500')}>
                    Payant
                  </button>
                </div>
                {tarifType === 'PAYANT' && (
                  <input type="number" step="0.01" value={prix}
                         onChange={e => setPrix(e.target.value)}
                         placeholder="Montant DH"
                         className="w-full mt-2 p-2 border border-amber-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-500" />
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-stone-700 mb-1 block">📅 Date promesse</label>
                <input type="date" value={datePromise}
                       onChange={e => setDatePromise(e.target.value)}
                       className="w-full p-2 border border-amber-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-500 outline-none" />
              </div>
            </div>

            {/* Photos compact */}
            <div>
              <label className="text-xs font-semibold text-gray-700 mb-1 block flex items-center gap-1.5">
                <Camera className="w-3.5 h-3.5" /> Photos AVANT (max 3)
              </label>
              <div className="flex gap-2 flex-wrap">
                {photos.map((p, i) => (
                  <div key={i} className="relative w-16 h-16 bg-stone-200 rounded-lg overflow-hidden border-2 border-amber-300 hover:scale-105 transition-transform">
                    <img src={URL.createObjectURL(p)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removePhoto(i)}
                            className="absolute top-0 right-0 bg-rose-500 text-white text-xs w-5 h-5 leading-3 rounded-bl hover:bg-rose-600">×</button>
                  </div>
                ))}
                {photos.length < 3 && (
                  <label className="w-16 h-16 border-2 border-dashed border-amber-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition-all">
                    <input type="file" accept="image/*" hidden onChange={handlePhotos} multiple />
                    <span className="text-2xl text-amber-500">+</span>
                  </label>
                )}
              </div>
            </div>

            {/* Notes admin compact */}
            <details className="text-xs">
              <summary className="cursor-pointer font-semibold text-stone-600 hover:text-stone-900 transition-colors">
                + Notes admin (optionnel, non visible ouvrier)
              </summary>
              <textarea rows="2" value={notesAdmin}
                        onChange={e => setNotesAdmin(e.target.value)}
                        placeholder="Notes internes..."
                        className="w-full mt-2 p-2 border border-amber-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-500 outline-none" />
            </details>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-sm">{error}</div>
          )}
        </div>

        {/* FOOTER */}
        <div className="px-6 py-4 border-t border-amber-200 bg-stone-50 rounded-b-2xl flex justify-end gap-2">
          <button onClick={onClose}
                  className="px-4 py-2.5 border border-stone-300 rounded-lg text-sm hover:bg-stone-100 transition-all">
            Annuler
          </button>
          <button onClick={submit} disabled={submitting}
                  className="px-7 py-2.5 bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 hover:from-amber-600 hover:via-yellow-600 hover:to-amber-700 text-white rounded-lg text-sm font-semibold tracking-wide shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:hover:shadow-lg">
            {submitting ? 'Creation...' : 'Creer le SAV ✨'}
          </button>
        </div>
      </div>
    </div>
  );
}
